import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@swot/base-components";

import { evidenceApi } from "../../services/evidenceApi.js";
import { complaintDropdownApi } from "../../services/complaintDropdownApi.js";
import { normalizeComplaintUiError } from "../../core/errors/cmtsComplaintErrorNormalizer.js";
import { buildEvidenceUploadMetadata } from "../../core/payloads/cmtsTypedPayloads.js";


const DEFAULT_ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

const DEFAULT_MAX_BYTES = 25 * 1024 * 1024;

const DANGEROUS_EXTENSIONS = new Set([
  "exe",
  "bat",
  "cmd",
  "js",
  "vbs",
  "ps1",
  "sh",
  "msi",
  "dll",
  "scr",
]);

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

export function validateEvidenceFile(
  file: File,
  options?: {
    readonly allowedTypes?: ReadonlySet<string>;
    readonly maxBytes?: number;
  },
): string | null {
  if (file.size <= 0) {
    return "Evidence file is empty.";
  }

  const extension = file.name.includes(".")
    ? file.name.split(".").pop()?.toLowerCase() ?? ""
    : "";

  if (
    extension &&
    DANGEROUS_EXTENSIONS.has(extension)
  ) {
    return "Dangerous executable evidence extensions are blocked before upload.";
  }

  const allowedTypes =
    options?.allowedTypes ?? DEFAULT_ALLOWED_TYPES;

  if (
    file.type &&
    !allowedTypes.has(file.type)
  ) {
    return "The selected file type is not allowed for complaint evidence.";
  }

  const maxBytes =
    options?.maxBytes ?? DEFAULT_MAX_BYTES;

  if (file.size > maxBytes) {
    return "The selected file exceeds the allowed evidence upload size.";
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getFileExtension(file: File): string {
  const extension = file.name.includes(".")
    ? file.name.split(".").pop()?.toUpperCase()
    : "";

  return extension || "FILE";
}

function getFileIcon(file: File): string {
  if (file.type === "application/pdf") {
    return "PDF";
  }

  if (file.type.startsWith("image/")) {
    return "IMG";
  }

  if (file.type.includes("spreadsheet")) {
    return "XLSX";
  }

  if (file.type.includes("wordprocessing")) {
    return "DOCX";
  }

  if (file.type === "text/plain") {
    return "TXT";
  }

  return getFileExtension(file);
}

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type UploadFileState = {
  readonly file: File;
  readonly progress: number;
  readonly status: "pending" | "uploading" | "completed" | "failed";
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export function CmtsEvidenceUploader({
  complaintId,
  onUploaded,
}: {
  readonly complaintId: string;
  readonly onUploaded: () => void | Promise<void>;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [confidentiality, setConfidentiality] = useState("");

  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [error, setError] = useState<unknown>(null);
  const [uploaded, setUploaded] = useState(false);

  const [uploadStates, setUploadStates] =
    useState<UploadFileState[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  /* ------------------------------------------------------------------------ */
  /* Confidentiality dropdown                                                */
  /* ------------------------------------------------------------------------ */

  const confidentialityOptions = useQuery({
    queryKey: [
      "cmts",
      "backend-evidence-confidentiality-options",
    ],

    queryFn: ({ signal }) =>
      complaintDropdownApi.master(
        "confidentiality",
        {
          activeOnly: true,
        },
        signal,
      ),

    retry: 1,
  });

  const confidentialityValues = useMemo(
    () =>
      new Set(
        (confidentialityOptions.data ?? []).map(
          (option) => option.value,
        ),
      ),
    [confidentialityOptions.data],
  );

  /* ------------------------------------------------------------------------ */
  /* Validation                                                               */
  /* ------------------------------------------------------------------------ */

  const validationError = useMemo(() => {
    if (!files.length) {
      return null;
    }

    const invalidFile = files
      .map((file) => ({
        file,
        error: validateEvidenceFile(file),
      }))
      .find((item) => item.error);

    if (invalidFile) {
      return `${invalidFile.file.name}: ${invalidFile.error}`;
    }

    if (confidentialityOptions.isError) {
      return "Evidence confidentiality reference data could not be loaded.";
    }

    if (
      !confidentialityOptions.isLoading &&
      confidentialityValues.size === 0
    ) {
      return "Evidence confidentiality reference data is empty.";
    }

    if (!confidentiality.trim()) {
      return "Evidence confidentiality is required and must come from backend reference data.";
    }

    if (!confidentialityValues.has(confidentiality)) {
      return "Evidence confidentiality is not present in backend reference data.";
    }

    return null;
  }, [
    files,
    confidentiality,
    confidentialityOptions.isError,
    confidentialityOptions.isLoading,
    confidentialityValues,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Backend error                                                            */
  /* ------------------------------------------------------------------------ */

  const uiError = error
    ? normalizeComplaintUiError(
        error,
        "Evidence upload failed.",
      )
    : null;

  const uiErrorDetail =
    uiError &&
    (uiError.fieldErrors?.length ||
      uiError.correlationId)
      ? `${uiError.fieldErrors?.[0]
          ? `Field: ${uiError.fieldErrors[0].field} — ${uiError.fieldErrors[0].message}. `
          : ""}${
          uiError.correlationId
            ? `Correlation ID: ${uiError.correlationId}`
            : ""
        }`
      : null;

  /* ------------------------------------------------------------------------ */
  /* File handling                                                            */
  /* ------------------------------------------------------------------------ */

  const handleFiles = (selectedFiles: File[]) => {
    setError(null);
    setUploaded(false);
    setProgress(0);

    if (!selectedFiles.length) {
      setFiles([]);
      setUploadStates([]);
      return;
    }

    const invalidFile = selectedFiles
      .map((file) => ({
        file,
        error: validateEvidenceFile(file),
      }))
      .find((item) => item.error);

    if (invalidFile) {
      setError(
        new Error(
          `${invalidFile.file.name}: ${invalidFile.error}`,
        ),
      );

      setFiles([]);
      setUploadStates([]);

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      return;
    }

    setFiles(selectedFiles);

    setUploadStates(
      selectedFiles.map((file) => ({
        file,
        progress: 0,
        status: "pending",
      })),
    );
  };

  const onFileChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    handleFiles(
      Array.from(event.currentTarget.files ?? []),
    );
  };

  const onDrop = (
    event: DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();

    if (busy) {
      return;
    }

    setDragActive(false);

    handleFiles(
      Array.from(event.dataTransfer.files ?? []),
    );
  };

  const removeFile = (index: number) => {
    if (busy) {
      return;
    }

    setFiles((current) =>
      current.filter(
        (_, fileIndex) => fileIndex !== index,
      ),
    );

    setUploadStates((current) =>
      current.filter(
        (_, fileIndex) => fileIndex !== index,
      ),
    );

    setProgress(0);
    setError(null);
    setUploaded(false);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const removeAllFiles = () => {
    if (busy) {
      return;
    }

    setFiles([]);
    setUploadStates([]);
    setProgress(0);
    setError(null);
    setUploaded(false);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Upload                                                                   */
  /* ------------------------------------------------------------------------ */

  const upload = async () => {
    if (!files.length) {
      setError(
        new Error("No evidence files selected."),
      );
      return;
    }

    const invalidFile = files
      .map((file) => ({
        file,
        error: validateEvidenceFile(file),
      }))
      .find((item) => item.error);

    if (invalidFile) {
      setError(
        new Error(
          `${invalidFile.file.name}: ${invalidFile.error}`,
        ),
      );
      return;
    }

    if (validationError) {
      setError(new Error(validationError));
      return;
    }

    setBusy(true);
    setError(null);
    setUploaded(false);
    setProgress(0);

    const totalBytes = files.reduce(
      (total, file) => total + file.size,
      0,
    );

    let completedBytes = 0;

    try {
for (
  let index = 0;
  index < files.length;
  index++
) {
  const currentFile = files[index];

  // Required because noUncheckedIndexedAccess
  if (!currentFile) {
    continue;
  }

  setUploadStates((current) =>
    current.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            status: "uploading",
            progress: 0,
          }
        : item,
    ),
  );

  await evidenceApi.uploadFile(
    complaintId,
    currentFile,
    {
      metadata: buildEvidenceUploadMetadata({
        description,
        confidentiality,
        file: currentFile,
      }),

      onUploadProgress: (event) => {
        const total =
          event.total ?? currentFile.size;

        const currentFileProgress =
          total > 0
            ? Math.min(
                100,
                Math.round(
                  (event.loaded / total) * 100,
                ),
              )
            : 0;

        setUploadStates((current) =>
          current.map((item, itemIndex) =>
            itemIndex === index
              ? {
                  ...item,
                  progress: currentFileProgress,
                  status: "uploading",
                }
              : item,
          ),
        );

        const currentLoaded = event.loaded;

        const overallLoaded =
          completedBytes + currentLoaded;

        if (totalBytes > 0) {
          setProgress(
            Math.min(
              100,
              Math.round(
                (overallLoaded / totalBytes) * 100,
              ),
            ),
          );
        }
      },
    },
  );

  completedBytes += currentFile.size;

  setUploadStates((current) =>
    current.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            progress: 100,
            status: "completed",
          }
        : item,
    ),
  );

  if (totalBytes > 0) {
    setProgress(
      Math.min(
        100,
        Math.round(
          (completedBytes / totalBytes) * 100,
        ),
      ),
    );
  }
}
      setProgress(100);
      setUploaded(true);

      setFiles([]);
      setUploadStates([]);

      setDescription("");
      setConfidentiality("");

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      await onUploaded();
    } catch (uploadError) {
      setError(uploadError);

      setUploadStates((current) =>
        current.map((item) =>
          item.status === "uploading"
            ? {
                ...item,
                status: "failed",
              }
            : item,
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Card
      title=""
      subtitle=""
    >
      <div className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">

        {/* ================================================================== */}
        {/* HEADER                                                             */}
        {/* ================================================================== */}

        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 px-6 py-7 sm:px-8">

          <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-indigo-500/20 blur-3xl" />

          <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">

            <div>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-2xl backdrop-blur-md">
                📎
              </div>

              <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
                Upload Evidence
              </h2>

              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">
                Securely attach one or multiple
                supporting documents, images and
                other evidence to this complaint.
              </p>
            </div>

            <div className="hidden rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-medium text-slate-200 backdrop-blur-sm sm:block">
              Max 25 MB / file
            </div>

          </div>
        </div>

        {/* ================================================================== */}
        {/* BODY                                                               */}
        {/* ================================================================== */}

        <div className="space-y-6 p-5 sm:p-8">

          {/* ERROR */}

          {uiError ? (
            <div className="cmts-evidence-fade-in rounded-2xl border border-red-200 bg-red-50 p-4">

              <div className="flex gap-3">

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-100 font-bold text-red-600">
                  !
                </div>

                <div className="min-w-0">

                  <p className="font-semibold text-red-800">
                    Evidence upload failed
                  </p>

                  <p className="mt-1 text-sm text-red-700">
                    {uiError.userMessage}
                  </p>

                  {uiErrorDetail ? (
                    <p className="mt-2 break-all text-xs text-red-600">
                      {uiErrorDetail}
                    </p>
                  ) : null}

                </div>

              </div>
            </div>
          ) : null}

          {/* SUCCESS */}

          {uploaded ? (
            <div className="cmts-evidence-fade-in rounded-2xl border border-emerald-200 bg-emerald-50 p-4">

              <div className="flex items-center gap-3">

                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-lg font-bold text-emerald-600">
                  ✓
                </div>

                <div>
                  <p className="font-semibold text-emerald-800">
                    Evidence uploaded successfully
                  </p>

                  <p className="text-sm text-emerald-700">
                    All selected evidence files have
                    been attached to this complaint.
                  </p>
                </div>

              </div>
            </div>
          ) : null}

          {/* ================================================================= */}
          {/* DROP ZONE                                                         */}
          {/* ================================================================= */}

          <div
            onDragEnter={(event) => {
              event.preventDefault();

              if (!busy) {
                setDragActive(true);
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();

              if (!busy) {
                setDragActive(true);
              }
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragActive(false);
            }}
            onDrop={onDrop}
            onClick={() => {
              if (!busy) {
                inputRef.current?.click();
              }
            }}
            className={`
              group relative cursor-pointer
              overflow-hidden rounded-3xl
              border-2 border-dashed p-7 text-center
              transition-all duration-300
              sm:p-10
              ${
                dragActive
                  ? "scale-[1.01] border-indigo-500 bg-indigo-50 shadow-xl shadow-indigo-100"
                  : files.length
                    ? "border-emerald-300 bg-emerald-50/30"
                    : "border-slate-300 bg-slate-50/70 hover:border-indigo-400 hover:bg-indigo-50/40 hover:shadow-lg"
              }
              ${busy ? "cursor-not-allowed opacity-70" : ""}
            `}
          >

            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={onFileChange}
              disabled={busy}
              className="hidden"
            />

            {/* =============================================================== */}
            {/* EMPTY STATE                                                      */}
            {/* =============================================================== */}

            {!files.length ? (
              <>
                <div
                  className={`
                    mx-auto flex h-16 w-16
                    items-center justify-center
                    rounded-2xl text-3xl
                    transition-all duration-300
                    ${
                      dragActive
                        ? "scale-110 bg-indigo-100"
                        : "bg-white shadow-md group-hover:-translate-y-1 group-hover:shadow-lg"
                    }
                  `}
                >
                  {dragActive ? "⬇️" : "☁️"}
                </div>

                <h3 className="mt-5 text-base font-bold text-slate-800 sm:text-lg">
                  {dragActive
                    ? "Drop your files here"
                    : "Drag & drop your evidence"}
                </h3>

                <p className="mt-2 text-sm text-slate-500">
                  or{" "}
                  <span className="font-semibold text-indigo-600">
                    browse from your computer
                  </span>
                </p>

                <p className="mt-2 text-xs text-slate-400">
                  You can select multiple files at once
                </p>

                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {[
                    "PDF",
                    "PNG",
                    "JPG",
                    "TXT",
                    "XLSX",
                    "DOCX",
                  ].map((type) => (
                    <span
                      key={type}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-500 shadow-sm"
                    >
                      {type}
                    </span>
                  ))}
                </div>

                <p className="mt-4 text-xs text-slate-400">
                  Maximum 25 MB per file
                </p>
              </>
            ) : (
              /* ============================================================= */
              /* SELECTED FILES                                                */
              /* ============================================================= */

              <div className="mx-auto max-w-3xl text-left">

                <div className="mb-4 flex items-center justify-between gap-3">

                  <div>
                    <p className="font-semibold text-slate-800">
                      {files.length} evidence{" "}
                      {files.length === 1
                        ? "file"
                        : "files"}{" "}
                      selected
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      All selected files passed
                      local validation.
                    </p>
                  </div>

                  {!busy ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeAllFiles();
                      }}
                      className="
                        rounded-xl px-3 py-2
                        text-xs font-semibold
                        text-red-500
                        transition
                        hover:bg-red-50
                        hover:text-red-600
                      "
                    >
                      Remove all
                    </button>
                  ) : null}

                </div>

                <div className="max-h-80 space-y-2 overflow-y-auto pr-1">

                  {files.map(
                    (selectedFile, index) => {
                      const state =
                        uploadStates[index];

                      return (
                        <div
                          key={`${selectedFile.name}-${selectedFile.lastModified}-${index}`}
                          onClick={(event) =>
                            event.stopPropagation()
                          }
                          className="
                            rounded-2xl
                            border border-slate-200
                            bg-white p-3
                            shadow-sm
                            transition-all
                            duration-200
                            hover:shadow-md
                          "
                        >

                          <div className="flex items-center gap-3">

                            <div className="
                              flex h-11 w-11 shrink-0
                              items-center justify-center
                              rounded-xl
                              bg-indigo-50
                              text-[10px]
                              font-black
                              text-indigo-600
                            ">
                              {getFileIcon(
                                selectedFile,
                              )}
                            </div>

                            <div className="min-w-0 flex-1">

                              <p className="truncate text-sm font-semibold text-slate-800">
                                {selectedFile.name}
                              </p>

                              <p className="mt-1 text-xs text-slate-500">
                                {formatFileSize(
                                  selectedFile.size,
                                )}
                              </p>

                            </div>

                            {state?.status ===
                            "completed" ? (
                              <span className="
                                rounded-lg
                                bg-emerald-50
                                px-2 py-1
                                text-[10px]
                                font-bold
                                text-emerald-600
                              ">
                                Uploaded
                              </span>
                            ) : state?.status ===
                              "failed" ? (
                              <span className="
                                rounded-lg
                                bg-red-50
                                px-2 py-1
                                text-[10px]
                                font-bold
                                text-red-600
                              ">
                                Failed
                              </span>
                            ) : !busy ? (
                              <button
                                type="button"
                                onClick={() =>
                                  removeFile(index)
                                }
                                className="
                                  rounded-lg
                                  px-2 py-1
                                  text-xs
                                  text-slate-400
                                  transition
                                  hover:bg-red-50
                                  hover:text-red-600
                                "
                              >
                                Remove
                              </button>
                            ) : null}

                          </div>

                          {busy &&
                          state?.status ===
                            "uploading" ? (
                            <div className="mt-3">

                              <div className="mb-1 flex justify-between text-[10px] font-semibold text-slate-500">
                                <span>
                                  Uploading...
                                </span>

                                <span>
                                  {state.progress}%
                                </span>
                              </div>

                              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">

                                <div
                                  className="
                                    h-full rounded-full
                                    bg-gradient-to-r
                                    from-indigo-500
                                    via-blue-500
                                    to-cyan-400
                                    transition-all
                                    duration-300
                                  "
                                  style={{
                                    width: `${state.progress}%`,
                                  }}
                                />

                              </div>
                            </div>
                          ) : null}

                        </div>
                      );
                    },
                  )}

                </div>

              </div>
            )}

          </div>

          {/* ================================================================= */}
          {/* FORM                                                              */}
          {/* ================================================================= */}

          <div className="grid gap-5 lg:grid-cols-2">

            {/* DESCRIPTION */}

            <div className="lg:col-span-2">

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Description
                <span className="ml-1 font-normal text-slate-400">
                  (Optional)
                </span>
              </label>

              <textarea
                value={description}
                onChange={(event) =>
                  setDescription(
                    event.currentTarget.value,
                  )
                }
                rows={4}
                disabled={busy}
                placeholder="Describe what this evidence contains and why it is relevant..."
                className="
                  w-full resize-none
                  rounded-2xl
                  border border-slate-200
                  bg-slate-50
                  px-4 py-3.5
                  text-sm text-slate-800
                  outline-none
                  transition-all duration-200
                  placeholder:text-slate-400
                  focus:border-indigo-400
                  focus:bg-white
                  focus:ring-4
                  focus:ring-indigo-100
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                "
              />

            </div>

            {/* CONFIDENTIALITY */}

            <div>

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Confidentiality
                <span className="ml-1 text-red-500">
                  *
                </span>
              </label>

              <div className="relative">

                <select
                  value={confidentiality}
                  onChange={(event) =>
                    setConfidentiality(
                      event.currentTarget.value,
                    )
                  }
                  disabled={
                    busy ||
                    confidentialityOptions.isLoading
                  }
                  className="
                    w-full appearance-none
                    rounded-2xl
                    border border-slate-200
                    bg-slate-50
                    px-4 py-3.5 pr-10
                    text-sm text-slate-700
                    outline-none
                    transition-all duration-200
                    focus:border-indigo-400
                    focus:bg-white
                    focus:ring-4
                    focus:ring-indigo-100
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                  "
                >

                  <option value="">
                    Select confidentiality
                  </option>

                  {(
                    confidentialityOptions.data ??
                    []
                  ).map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}

                </select>

                <div className="
                  pointer-events-none
                  absolute right-4
                  top-1/2
                  -translate-y-1/2
                  text-slate-400
                ">
                  ↓
                </div>

              </div>

              <p className="mt-2 text-xs text-slate-400">
                Select a value provided by backend
                reference data.
              </p>

            </div>

            {/* SECURITY */}

            <div className="
              flex items-start gap-3
              rounded-2xl
              border border-slate-200
              bg-slate-50
              p-4
            ">

              <div className="
                flex h-9 w-9 shrink-0
                items-center justify-center
                rounded-xl
                bg-white
                shadow-sm
              ">
                🔒
              </div>

              <div>

                <p className="text-sm font-semibold text-slate-700">
                  Secure evidence upload
                </p>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Each file is validated for type,
                  size and dangerous extensions
                  before being sent to the backend.
                </p>

              </div>

            </div>

          </div>

          {/* ================================================================= */}
          {/* VALIDATION                                                        */}
          {/* ================================================================= */}

          {validationError ? (
            <div className="cmts-evidence-fade-in rounded-2xl border border-amber-200 bg-amber-50 p-4">

              <div className="flex items-start gap-3">

                <span className="text-lg">
                  ⚠️
                </span>

                <div>

                  <p className="text-sm font-semibold text-amber-800">
                    File blocked before upload
                  </p>

                  <p className="mt-1 text-sm text-amber-700">
                    {validationError}
                  </p>

                </div>

              </div>

            </div>
          ) : null}

          {/* ================================================================= */}
          {/* OVERALL PROGRESS                                                  */}
          {/* ================================================================= */}

          {busy || progress > 0 ? (
            <div className="
              rounded-2xl
              border border-slate-200
              bg-slate-50
              p-5
            ">

              <div className="mb-3 flex items-center justify-between">

                <span className="text-sm font-semibold text-slate-700">
                  {progress >= 100
                    ? "Upload complete"
                    : `Uploading ${files.length} evidence ${
                        files.length === 1
                          ? "file"
                          : "files"
                      }...`}
                </span>

                <span className="text-sm font-bold text-indigo-600">
                  {progress}%
                </span>

              </div>

              <div className="
                h-2.5
                overflow-hidden
                rounded-full
                bg-slate-200
              ">

                <div
                  className="
                    cmts-evidence-progress
                    relative
                    h-full
                    rounded-full
                    bg-gradient-to-r
                    from-indigo-500
                    via-blue-500
                    to-cyan-400
                    transition-all
                    duration-300
                  "
                  style={{
                    width: `${progress}%`,
                  }}
                />

              </div>

            </div>
          ) : null}

          {/* ================================================================= */}
          {/* FOOTER                                                            */}
          {/* ================================================================= */}

          <div className="
            flex flex-col gap-4
            border-t border-slate-100
            pt-6
            sm:flex-row
            sm:items-center
            sm:justify-between
          ">

            <div className="text-xs text-slate-400">

              <span className="font-medium text-slate-500">
                Supported:
              </span>{" "}
              PDF, PNG, JPG, TXT, XLSX, DOCX

            </div>

            <button
              type="button"
              onClick={upload}
              disabled={
                busy ||
                files.length === 0 ||
                Boolean(validationError)
              }
              className="
                group
                relative
                inline-flex
                items-center
                justify-center
                gap-2
                overflow-hidden
                rounded-2xl
                bg-slate-900
                px-6 py-3.5
                text-sm
                font-semibold
                text-white
                shadow-lg
                shadow-slate-900/20
                transition-all
                duration-300
                hover:-translate-y-0.5
                hover:bg-indigo-600
                hover:shadow-xl
                hover:shadow-indigo-500/20
                disabled:cursor-not-allowed
                disabled:bg-slate-300
                disabled:shadow-none
                disabled:hover:translate-y-0
              "
            >

              {busy ? (
                <>
                  <span className="
                    h-4 w-4
                    animate-spin
                    rounded-full
                    border-2
                    border-white/30
                    border-t-white
                  " />

                  Uploading...
                </>
              ) : (
                <>
                  <span className="
                    text-lg
                    transition-transform
                    duration-300
                    group-hover:-translate-y-0.5
                  ">
                    ↑
                  </span>

                  Upload{" "}
                  {files.length > 1
                    ? `${files.length} Evidence Files`
                    : "Evidence"}
                </>
              )}

            </button>

          </div>

        </div>
      </div>
    </Card>
  );
}